import { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Textarea from '@cloudscape-design/components/textarea';
import { createOverride } from './api';

interface Props {
  idToken: string;
  onDismiss: () => void;
  onCreated: () => void;
}

export default function AddOverrideModal({ idToken, onDismiss, onCreated }: Props) {
  const [alias, setAlias] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    // Guard against re-entrancy: a rapid second Enter can fire before the
    // saving state re-renders and disables the controls.
    if (saving) return;
    const trimmed = alias.trim();
    if (!trimmed) {
      setError('Enter a tester alias.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createOverride(idToken, trimmed, comment.trim());
      onCreated();
    } catch (e) {
      // The backend validates the alias (real employee, reports to a pillar
      // manager, not a duplicate) and returns a human-readable reason.
      setError(e instanceof Error ? e.message : 'Failed to create override.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Add Override"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} onClick={() => void handleCreate()}>
              Add
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField
          label="Tester alias"
          description="The person must be a valid Amazon employee who reports to one of the three pillar managers."
          errorText={error || undefined}
        >
          <Input
            value={alias}
            placeholder="e.g. jdoe"
            autoFocus
            onChange={({ detail }) => {
              setAlias(detail.value);
              if (error) setError('');
            }}
            onKeyDown={({ detail }) => {
              if (detail.key === 'Enter' && !saving) void handleCreate();
            }}
          />
        </FormField>
        <FormField label="Comment" description="Optional note explaining why this override exists.">
          <Textarea
            value={comment}
            placeholder="e.g. On rotation until Q3; excluded per manager request."
            onChange={({ detail }) => setComment(detail.value)}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
